"use client"

import { useEffect } from "react"
import styles from "./modal.module.css"

const Modal = ({
  title,
  children,
  footer,
  isOpen: externalIsOpen,
  onClose,
  width = "700px",
  height = "80vh",
  headerButtons = [],
  size = "medium",
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
}) => {
  useEffect(() => {
    if (!externalIsOpen || !closeOnEscape) return

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [externalIsOpen, closeOnEscape, onClose])

  // 🚀 Disable background scroll when modal open
  useEffect(() => {
    if (externalIsOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    // cleanup
    return () => {
      document.body.style.overflow = ""
    }
  }, [externalIsOpen])

  const handleOverlayClick = (event) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose()
    }
  }

  const renderHeaderButton = (button, index) => {
    const buttonClasses = [styles.modal_header_btn, button.variant && styles[`btn_${button.variant}`]]
      .filter(Boolean)
      .join(" ")

    return (
      <button
        key={index}
        className={buttonClasses}
        onClick={button.onClick}
        disabled={button.disabled}
        type="button"
        title={button.tooltip}
      >
        {button.icon && <span className={styles.btn_icon}>{button.icon}</span>}
        {button.label}
      </button>
    )
  }

  if (!externalIsOpen) return null

  return (
    <div
      className={styles.modal_overlay}
      onClick={handleOverlayClick}
      style={{
        "--modal-width": width,
        "--modal-height": height,
      }}
    >
      <div className={`${styles.modal_container} ${styles[`modal_${size}`]}`}>
        <div className={styles.modal_header}>
          <div className={styles.modal_title_section}>
            {title && <h2 className={styles.modal_title}>{title}</h2>}
          </div>

          <div className={styles.modal_header_actions}>
            {headerButtons.map((button, index) => renderHeaderButton(button, index))}

            {showCloseButton && (
              <button
                className={styles.modal_close_btn}
                onClick={onClose}
                type="button"
                aria-label="Close modal"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className={styles.modal_body}>{children}</div>

        {footer && <div className={styles.modal_footer}>{footer}</div>}
      </div>
    </div>
  )
}

export default Modal
